import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import MathquillUIElement from 'mathquill-ui';

import { t } from '../../../../services/i18nService';
import stringUtils from '../../../../qti/utils/StringUtils';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';

import styles from './mathWorking.scss';

const MAX_CONTENT_WIDTH = 700;

class MathWorkingInput extends React.PureComponent {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        extraChildren: PropTypes.object,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        mathRowModel: PropTypes.object,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        onInstanceReady: PropTypes.func,
        userResponse: PropTypes.array.isRequired,
        parent: PropTypes.object.isRequired, // TextEntryInteraction || MathWorkingInteraction instance
        mathSymbolPalettePreset: PropTypes.object,
    };

    static defaultProps = {
        mathRowModel: {},
    };

    constructor(props) {
        super(props);
        this.state = { isTooWide: false };
    }

    componentDidMount() {
        this.initMathquill();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        if (this.props.isReadOnly !== nextProps.isReadOnly) {
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
        }
    }

    componentDidUpdate(prevProps) {
        const { isReadOnly = false } = this.props;
        if (isReadOnly) {
            this.initMathquill();
        } else if (!isReadOnly && prevProps.isReadOnly) {
            // changed from readonly to editable
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
            this.initMathquill();
        } else if (this.props.userResponse.length === 0 && prevProps.userResponse.length > 0) {
            // changed userResponses (reset of question)
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
            this.initMathquill();
        }
    }

    componentWillUnmount() {
        if (this.mathQuillTargetElement) {
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
        }
    }

    initMathquill = () => {
        if (typeof this.props.onInstanceReady === 'function') {
            this.props.onInstanceReady(this);
        }

        if (this.props.mathRowModel) {
            this.props.mathRowModel.instance = this;
        }
        const { isReadOnly = false, mathSymbolPalettePreset } = this.props;
        let mathquillInitText = this.props.parent.getUserResponse();
        mathquillInitText = (this.props.model.variant === 'math' && this.props.mathRowModel)
            ? this.props.mathRowModel.mathText
            : mathquillInitText;
        const isA11yIIBScreenReaderSupportedV3 = a11yIIBScreenReaderService
            .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);

        if (isReadOnly) {
            ReactDOM.render(
                <MathquillUIElement
                    key={`mathquill-${this.props.model.identifier}`}
                    triggerOnClick={false}
                    isStaticMathElement
                    id={this.getMathEditableId()}
                    mathquillText={mathquillInitText}
                    onInstanceReady={this.onMathquillUIElementInstanceReady}
                    isA11yIIBScreenReaderEnabled={isA11yIIBScreenReaderSupportedV3}
                />,
                this.mathQuillTargetElement
            );
            this.setMathInputSize();
        } else {
            ReactDOM.render(
                <MathquillUIElement
                    key={`mathquill-${this.props.model.identifier}`}
                    triggerOnClick={false}
                    disableMenuCache
                    id={this.getMathEditableId()}
                    wrapper={this.mathQuillTargetElement}
                    extraChildren={this.props.extraChildren}
                    mathquillText={mathquillInitText}
                    mathEditableChangeCallback={this.onMathEditableChange}
                    onInstanceReady={this.onMathquillUIElementInstanceReady}
                    menuData={mathSymbolPalettePreset}
                    isA11yIIBScreenReaderEnabled={isA11yIIBScreenReaderSupportedV3}
                />,
                this.mathQuillTargetElement
            );
        }
    };

    handleUserResponse = () => {
        const { model } = this.props;
        if (typeof this.props.parent.handleUserResponse === 'function') {
            this.props.parent.handleUserResponse();
        } else {
            this.props.callback(model.identifier, [this.getValue()]);
        }
    };

    setValue = (userResponse) => {
        if (this.mathquillUIElement) {
            this.mathquillUIElement.setMathText(userResponse);
        }
    };

    getValue = () => {
        return this.mathquillUIElement ? this.mathquillUIElement.getLatex() : '';
    };

    getRenderedContentWidth = () => {
        return this.mathquillUIElement ? this.mathquillUIElement.getRenderedContentWidth() : 0;
    };

    getMathEditableId = () => {
        if (this.mathEditableId) {
            // use existing (cached) value of this.mathEditableId
        } else if (this.props.mathRowModel.id) {
            this.mathEditableId = this.props.mathRowModel.id;
        } else if (this.props.model.variant === 'math') {
            this.mathEditableId = stringUtils.getUID();
        } else {
            this.mathEditableId = `${this.props.model.target}_valueId`;
        }

        return this.mathEditableId;
    };

    onMathEditableChange = () => {
        if (this.props.mathRowModel) {
            this.props.mathRowModel.mathText = this.getValue();
        }
        const width = this.getRenderedContentWidth();
        this.setState({ isTooWide: width > MAX_CONTENT_WIDTH });
        this.handleUserResponse();
    };

    onMathquillUIElementInstanceReady = (instance) => {
        this.mathquillUIElement = instance;
        if (this.props.mathRowModel && this.props.mathRowModel.autoFocus) {
            instance.onMathEditorClicked();
            this.props.mathRowModel.autoFocus = false;
        }
        this.setMathInputSize();
        this.props.handleInteractionReady(true);
    };

    setMathInputSize = () => {
        const { isReadOnly = false } = this.props;
        const inputSize = `${MAX_CONTENT_WIDTH}px`;
        if (isReadOnly) {
            this.mathQuillTargetElement.style.minWidth = inputSize;
            return;
        }

        const editableElement = this.mathQuillTargetElement.querySelector('.mathquill-editable');
        if (editableElement) {
            editableElement.style.width = inputSize;
        }
    };

    render() {
        const { isReadOnly = false } = this.props;
        const { isTooWide } = this.state;

        if (isReadOnly) {
            return <span className="mathquill-ui-static" ref={(div) => { this.mathQuillTargetElement = div; }} />;
        }

        const rootClassNames = `interaction mathquill-outer-wrapper ${isTooWide ? styles.isTooWide : ''}`;
        return (
            <span className={rootClassNames}>
                {
                    isTooWide ?
                        <span className={styles.message}>
                            {t('interactions.mathWorking.tooLongWarning')}
                        </span> : null
                }
                <span className="mathquill-ui-wrapper" ref={(div) => { this.mathQuillTargetElement = div; }} />
            </span>
        );
    }
}

export default MathWorkingInput;
